import { useState } from "react";
import { C, Card, CardHead, SectionTitle, Btn, Badge, Bar, Dot, Modal, Field, GpuMini, statusBadge, sC, uc } from "./theme";
import { GET, POST, PATCH, DELETE } from "./api";
import { useLang } from "./i18n";

export default function AdminUsers(props) {
  const { users, vms, fetchAll } = props;
  const { t } = useLang();
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ id: "", name: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [pwTarget, setPwTarget] = useState(null);
  const [newPw, setNewPw] = useState("");

  const vmCountMap = {};
  vms.forEach(v => {
    vmCountMap[v.user_id] = (vmCountMap[v.user_id] || 0) + 1;
  });

  const filtered = users.filter(u =>
    u.id.toLowerCase().includes(search.toLowerCase()) ||
    u.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleAdd = async () => {
    if (!form.id || !form.name || !form.password) return;
    setBusy(true);
    try {
      await POST("/api/users", { id: form.id.toLowerCase(), name: form.name, password: form.password });
      setShowAdd(false);
      setForm({ id: "", name: "", password: "" });
      fetchAll();
    } catch (e) {
      alert("사용자 생성 실패: " + (e.message || e));
    }
    setBusy(false);
  };

  const toggleActive = async (u) => {
    try {
      await PATCH(`/api/users/${u.id}`, { active: !u.active });
      fetchAll();
    } catch (e) {
      alert("상태 변경 실패: " + (e.message || e));
    }
  };

  const handlePwChange = async () => {
    if (!newPw) return;
    try {
      await PATCH(`/api/users/${pwTarget.id}/password`, { password: newPw });
      setPwTarget(null);
      setNewPw("");
    } catch (e) {
      alert("비밀번호 변경 실패: " + (e.message || e));
    }
  };

  const handleDelete = async (u) => {
    const vmCount = vmCountMap[u.id] || 0;
    const warn = vmCount > 0
      ? `\n\n⚠ 이 사용자의 VM ${vmCount}개와 모든 디스크 데이터가 영구 삭제됩니다 (복구 불가).`
      : "";
    if (!window.confirm(`정말로 사용자 "${u.id}" (${u.name})를 삭제하시겠습니까?${warn}`)) return;
    try {
      await DELETE(`/api/users/${u.id}`);
      fetchAll();
    } catch (e) {
      alert("삭제 실패: " + (e.message || e));
    }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("ko-KR") : "-";

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <SectionTitle style={{ margin: 0 }}>{t("user_management")}</SectionTitle>
        <Btn onClick={() => setShowAdd(true)} style={{ background: C.accent }}>+ {t("add_user")}</Btn>
      </div>

      <div style={{ marginBottom: 12 }}>
        <input
          placeholder={t("search")}
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ background: C.bg2, border: `1px solid ${C.border}`, color: C.fg, padding: "6px 12px", borderRadius: 4, width: 260, fontSize: 13 }}
        />
      </div>

      <Card style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.border}`, color: C.dim, fontSize: 11, textTransform: "uppercase" }}>
              <th style={thS}>{t("user_id")}</th>
              <th style={thS}>{t("user_name")}</th>
              <th style={thS}>{t("status")}</th>
              <th style={thS}>{t("vm")}</th>
              <th style={thS}>{t("created_at")}</th>
              <th style={thS}>{t("role")}</th>
              <th style={{ ...thS, textAlign: "right" }}>{t("actions")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(u => (
              <tr key={u.id} style={{ borderBottom: `1px solid ${C.border}11` }}>
                <td style={tdS}><span style={{ color: C.accent, fontWeight: 600 }}>{u.id}</span></td>
                <td style={tdS}>{u.name}</td>
                <td style={tdS}>
                  <Badge style={{ background: u.active ? C.green + "22" : C.red + "22", color: u.active ? C.green : C.red, fontSize: 10 }}>
                    {u.active ? t("active") : t("inactive")}
                  </Badge>
                </td>
                <td style={tdS}>{vmCountMap[u.id] || 0}</td>
                <td style={tdS}><span style={{ color: C.dim }}>{fmtDate(u.created_at)}</span></td>
                <td style={tdS}>
                  <Badge style={{ background: u.role === "admin" ? C.accent + "22" : C.bg2, color: u.role === "admin" ? C.accent : C.dim, fontSize: 10 }}>
                    {u.role || "user"}
                  </Badge>
                </td>
                <td style={{ ...tdS, textAlign: "right" }}>
                  <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                    <Btn onClick={() => toggleActive(u)} style={{ fontSize: 11, padding: "3px 8px", background: u.active ? C.red + "33" : C.green + "33", color: u.active ? C.red : C.green }}>
                      {u.active ? t("inactive") : t("active")}
                    </Btn>
                    <Btn onClick={() => { setPwTarget(u); setNewPw(""); }} style={{ fontSize: 11, padding: "3px 8px", background: C.amber + "22", color: C.amber }}>
                      비밀번호
                    </Btn>
                    <Btn onClick={() => handleDelete(u)} style={{ fontSize: 11, padding: "3px 8px", background: C.red + "22", color: C.red }}>
                      {t("delete")}
                    </Btn>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} style={{ ...tdS, textAlign: "center", color: C.dim }}>{t("none")}</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      {/* Change password modal */}
      {pwTarget && (
        <Modal onClose={() => setPwTarget(null)} title={`비밀번호 변경 — ${pwTarget.id}`}>
          <div style={{ marginBottom: 13 }}>
            <div style={{ fontSize: 9, color: C.dim2, fontFamily: "monospace", letterSpacing: 1, marginBottom: 5, textTransform: "uppercase" }}>새 비밀번호</div>
            <input type="password" autoComplete="new-password" value={newPw} onChange={e => setNewPw(e.target.value)} style={inputS} />
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
            <Btn onClick={() => setPwTarget(null)} style={{ background: C.bg2 }}>{t("cancel")}</Btn>
            <Btn onClick={handlePwChange} style={{ background: C.amber }}>변경</Btn>
          </div>
        </Modal>
      )}

      {/* Add user modal */}
      {showAdd && (
        <Modal onClose={() => setShowAdd(false)} title={t("add_user")}>
          <Field label="ID" value={form.id} onChange={v => setForm({ ...form, id: v.toLowerCase().replace(/[^a-z0-9_-]/g, "") })} placeholder="예: alice" />
          <Field label={t("user_name")} value={form.name} onChange={v => setForm({ ...form, name: v })} />
          <Field label={t("password")} value={form.password} onChange={v => setForm({ ...form, password: v })} type="password" />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
            <Btn onClick={() => setShowAdd(false)} style={{ background: C.bg2 }}>{t("cancel")}</Btn>
            <Btn onClick={handleAdd} disabled={busy} style={{ background: C.accent }}>{busy ? t("creating") : t("add_user")}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

const thS = { textAlign: "left", padding: "8px 10px", fontWeight: 600 };
const tdS = { padding: "8px 10px", color: C.fg };
const inputS = { width: "100%", background: C.bg2, border: `1px solid ${C.border}`, color: C.fg, padding: "7px 10px", borderRadius: 4, fontSize: 13 };
